import React, { useMemo, useState } from 'react';
import { Avatar, Box, Chip, TextField, Typography, makeStyles } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { SUPPORTED_EVM_CHAINS, chainLabel, chainIcon } from './chainConstants';

const useStyles = makeStyles((theme) => ({
  chipGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: theme.spacing(1.5),
  },
  chainChip: {
    backgroundColor: theme.name === 'LIGHT' ? 'rgba(125,86,231,0.10)' : 'rgba(125,86,231,0.16)',
    border: '1px solid rgba(125,86,231,0.45)',
    color: '#7D56E7',
    fontWeight: 600,
    '& .MuiChip-deleteIcon': {
      color: '#7D56E7',
      opacity: 0.7,
    },
    '& .MuiChip-deleteIcon:hover': {
      color: '#7D56E7',
      opacity: 1,
    },
  },
  addSectionLabel: {
    display: 'block',
    marginBottom: 6,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.name === 'LIGHT' ? '#83818E' : '#9C9AA5',
  },
  // Search field: a pill-shaped input with a leading icon, replacing the default outlined
  // MUI text field so this reads as its own designed control rather than a generic form field.
  searchInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: theme.name === 'LIGHT' ? '#F6F4FB' : 'rgba(255,255,255,0.04)',
      transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
      '& fieldset': {
        borderColor: theme.name === 'LIGHT' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(162, 89, 255, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
  },
  searchIcon: {
    fontSize: 18,
    color: theme.name === 'LIGHT' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)',
    marginRight: 4,
  },
  // Dropdown paper itself: rounded, shadowed, and scrollable rather than the default flat list.
  popperPaper: {
    borderRadius: 14,
    marginTop: 6,
    boxShadow: '0px 12px 32px rgba(0,0,0,0.18)',
    border: theme.name === 'LIGHT' ? '1px solid rgba(0,0,0,0.06)' : '1px solid rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  listbox: {
    padding: 6,
    maxHeight: 320,
  },
  option: {
    borderRadius: 10,
    padding: '8px 10px',
    gap: 10,
    fontFamily: '"Manrope", Helvetica, sans-serif',
    fontWeight: 500,
    '&[data-focus="true"]': {
      backgroundColor: 'rgba(125,86,231,0.12)',
    },
    '&[aria-selected="true"]': {
      backgroundColor: 'rgba(125,86,231,0.16)',
    },
  },
  optionAvatar: {
    width: 24,
    height: 24,
    fontSize: 11,
    fontWeight: 700,
    backgroundColor: '#5A5A5A',
  },
  noOptions: {
    fontFamily: '"Manrope", Helvetica, sans-serif',
    fontSize: 13,
  },
}));

// Chip grid + search-to-add control for picking a set of EVM chains. Used both by the add-wallet
// flow (TabView.js, where `chains` is the in-progress selection before the wallet exists) and the
// manage-chains dialog (Wallet.js, where `chains` is passed as [] to render just the "add another
// chain" search box, since already-selected chains are shown as their own rows there instead).
export default function ChainSelector({ chains, onAdd, onRemove, placeholder = 'Search chains to add…', addSectionLabel = null }) {
  const classes = useStyles();
  const [searchInput, setSearchInput] = useState('');

  const availableToAdd = useMemo(
    () =>
      SUPPORTED_EVM_CHAINS
        .filter((chain) => !chains.includes(chain))
        .sort((a, b) => chainLabel(a).localeCompare(chainLabel(b))),
    [chains]
  );

  return (
    <Box>
      {chains.length > 0 && (
        <Box className={classes.chipGrid}>
          {chains.map((chain) => (
            <Chip
              key={chain}
              avatar={
                chainIcon(chain) ? (
                  <Avatar src={chainIcon(chain)} />
                ) : (
                  <Avatar className={classes.optionAvatar}>{chainLabel(chain).charAt(0)}</Avatar>
                )
              }
              label={chainLabel(chain)}
              className={classes.chainChip}
              onDelete={() => onRemove(chain)}
            />
          ))}
        </Box>
      )}

      {addSectionLabel && (
        <Typography component="label" className={classes.addSectionLabel}>
          {addSectionLabel}
        </Typography>
      )}

      <Autocomplete
        options={availableToAdd}
        getOptionLabel={chainLabel}
        value={null}
        inputValue={searchInput}
        onInputChange={(event, newInputValue) => setSearchInput(newInputValue)}
        onChange={(event, chain) => {
          if (chain) {
            onAdd(chain);
          }
          setSearchInput('');
        }}
        classes={{
          paper: classes.popperPaper,
          listbox: classes.listbox,
          option: classes.option,
          noOptions: classes.noOptions,
        }}
        noOptionsText="No matching chains"
        renderOption={(chain) => {
          const icon = chainIcon(chain);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
              <Avatar
                src={icon || undefined}
                variant="circular"
                className={classes.optionAvatar}
                style={{ backgroundColor: icon ? 'transparent' : undefined }}
              >
                {!icon && chainLabel(chain).charAt(0)}
              </Avatar>
              <Typography variant="body2" style={{ fontFamily: '"Manrope", Helvetica, sans-serif', fontWeight: 500 }}>
                {chainLabel(chain)}
              </Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            className={classes.searchInput}
            InputProps={{
              ...params.InputProps,
              startAdornment: <SearchIcon className={classes.searchIcon} />,
            }}
          />
        )}
      />
    </Box>
  );
}
